import React, { useState } from 'react';
import { Scissors, ShieldCheck, Trash2, ArrowLeft, ArrowRight, Sparkles } from 'lucide-react';
import { toast } from './Toast';
import type { CutPlan, CutRange } from '@vidly/types';

interface CutPlanEditorProps {
  plan: CutPlan;
  totalDurationSeconds: number;
  onConfirmRender: (finalRanges: CutRange[]) => void;
  onRegenerate: () => void;
  isRendering: boolean;
}

export const CutPlanEditor: React.FC<CutPlanEditorProps> = ({
  plan,
  totalDurationSeconds,
  onConfirmRender,
  onRegenerate,
  isRendering
}) => {
  const [ranges, setRanges] = useState<CutRange[]>(plan.selectedRanges);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const currentDuration = ranges.reduce((acc, r) => acc + (r.end - r.start), 0);

  const handleUpdateRangeTime = (index: number, field: 'start' | 'end', value: number) => {
    const updated = [...ranges];
    updated[index] = {
      ...updated[index],
      [field]: Math.max(0, Math.min(totalDurationSeconds, value))
    };
    if (updated[index].end <= updated[index].start) {
      updated[index].end = updated[index].start + 2;
    }
    setRanges(updated);
  };

  const handleRemoveRange = (index: number) => {
    const target = ranges[index];
    if (target.isMandatory) {
      toast.warning('Intervalo protegido', 'Este intervalo fue marcado como obligatorio y no puede eliminarse.');
      return;
    }
    setRanges(ranges.filter((_, i) => i !== index));
    toast.info('Corte eliminado', 'Duración recalculada.');
  };

  return (
    <div className="space-y-6 text-left max-w-3xl mx-auto">
      
      {/* Top Banner */}
      <div className="p-4 sm:p-5 rounded-xl border border-zinc-800 bg-zinc-900/30 space-y-3 sm:space-y-0 sm:flex sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-zinc-300 shrink-0" />
            <h3 className="text-sm font-semibold text-white">Plan de cortes por IA</h3>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-zinc-800 text-zinc-300 border border-zinc-700">
              <ShieldCheck className="w-3 h-3" />
              Sincronizado con voz
            </span>
          </div>
          <p className="text-xs text-zinc-400 leading-relaxed">
            Cortes que respetan frases completas, ensamblados con micro-fades de audio.
          </p>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <div className="flex-1 sm:flex-none px-3 py-1.5 rounded-lg bg-zinc-900 border border-zinc-800 text-center sm:text-right">
            <span className="text-[10px] text-zinc-500 block">Estimado</span>
            <span className="text-sm font-semibold text-white">{formatTime(currentDuration)}</span>
          </div>
          <div className="flex-1 sm:flex-none px-3 py-1.5 rounded-lg bg-zinc-900 border border-zinc-800 text-center sm:text-right">
            <span className="text-[10px] text-zinc-500 block">Objetivo</span>
            <span className="text-sm font-semibold text-zinc-400">{formatTime(plan.targetDurationSeconds)}</span>
          </div>
        </div>
      </div>

      {/* Visual Timeline Bar */}
      <div className="p-4 rounded-xl border border-zinc-800 bg-zinc-900/30 space-y-2">
        <div className="flex justify-between text-xs text-zinc-400">
          <span>00:00</span>
          <span>Línea temporal del metraje ({formatTime(totalDurationSeconds)})</span>
          <span>{formatTime(totalDurationSeconds)}</span>
        </div>

        <div className="h-4 w-full rounded-full bg-zinc-950 border border-zinc-800 relative overflow-hidden flex">
          {ranges.map((range, idx) => {
            const leftPercent = (range.start / totalDurationSeconds) * 100;
            const widthPercent = ((range.end - range.start) / totalDurationSeconds) * 100;
            return (
              <div
                key={idx}
                title={`${range.reason} (${formatTime(range.start)} - ${formatTime(range.end)})`}
                className={`absolute h-full rounded-full transition-all ${
                  range.isMandatory
                    ? 'bg-emerald-400/90'
                    : 'bg-zinc-200/90'
                }`}
                style={{
                  left: `${leftPercent}%`,
                  width: `${Math.max(1.5, widthPercent)}%`
                }}
              />
            );
          })}
        </div>

        <div className="flex gap-4 text-[11px] text-zinc-500 pt-1">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block"></span>
            Protegido obligatorio
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-zinc-200 inline-block"></span>
            Seleccionado por IA
          </span>
        </div>
      </div>

      {/* Segments List */}
      <div className="space-y-2">
        <div className="flex justify-between items-center text-xs text-zinc-400 px-1">
          <span>Fragmentos seleccionados ({ranges.length})</span>
        </div>

        <div className="space-y-2">
          {ranges.map((range, idx) => (
            <div
              key={idx}
              className="p-3.5 rounded-xl border border-zinc-800/80 bg-zinc-900/30 flex flex-col gap-2.5 text-xs"
            >
              {/* Top row: index + badge + reason */}
              <div className="flex items-start gap-2.5">
                <span className="text-xs text-zinc-500 w-5 shrink-0 pt-0.5">#{idx + 1}</span>
                {range.isMandatory ? (
                  <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-zinc-800 text-zinc-200 border border-zinc-700 shrink-0">
                    Obligatorio
                  </span>
                ) : (
                  <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-zinc-800 text-zinc-300 shrink-0">
                    IA
                  </span>
                )}
                <span className="text-zinc-200 leading-snug">{range.reason}</span>
              </div>

              {/* Bottom row: time inputs + delete */}
              <div className="flex items-center gap-3 pl-7">
                <div className="flex items-center gap-1.5 text-zinc-300">
                  <input
                    type="number"
                    step="0.5"
                    value={parseFloat(Number(range.start).toFixed(1))}
                    onChange={(e) => handleUpdateRangeTime(idx, 'start', Number(e.target.value))}
                    className="w-16 bg-zinc-950 border border-zinc-800 rounded px-1.5 py-0.5 text-xs text-center font-mono"
                  />
                  <span>—</span>
                  <input
                    type="number"
                    step="0.5"
                    value={parseFloat(Number(range.end).toFixed(1))}
                    onChange={(e) => handleUpdateRangeTime(idx, 'end', Number(e.target.value))}
                    className="w-16 bg-zinc-950 border border-zinc-800 rounded px-1.5 py-0.5 text-xs text-center font-mono"
                  />
                  <span className="text-zinc-500 text-[11px] ml-1 font-mono">
                    ({formatTime(Math.max(0, range.end - range.start))})
                  </span>
                </div>

                {!range.isMandatory && (
                  <button
                    onClick={() => handleRemoveRange(idx)}
                    className="text-zinc-500 hover:text-red-400 transition-colors p-1 ml-auto"
                    title="Eliminar corte"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Actions */}
      <div className="flex flex-col sm:flex-row items-stretch gap-3 pt-2">
        <button
          onClick={onRegenerate}
          disabled={isRendering}
          className="btn-secondary py-2.5 px-4 text-xs font-medium flex items-center justify-center gap-1.5"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Volver a ajustes</span>
        </button>

        <button
          onClick={() => onConfirmRender(ranges)}
          disabled={isRendering}
          className="btn-primary py-2.5 px-6 text-xs font-medium flex-1 flex items-center justify-center gap-1.5"
        >
          <span>{isRendering ? 'Renderizando vídeo...' : 'Confirmar y renderizar con FFmpeg'}</span>
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>

    </div>
  );
};
